import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Shield, Users, Activity, Database, Building2, Settings2, Bot } from 'lucide-react';
import { Navigate, Link } from 'react-router-dom';
import { adminAPI, jobsAPI, filesAPI } from '../services/api';
import { theme } from '../styles/theme';
import { MetricTile, MetricTileGrid } from '../components/MetricTile';
import type { ProcessingJob, FileUpload, PaginatedResponse } from '../types';

const Admin: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalJobs: 0,
    completedJobs: 0,
    totalFiles: 0,
  });

  // Redirect non-admin users
  if (!user?.is_superuser) {
    return <Navigate to="/dashboard" replace />;
  }

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setLoading(true);
    try {
      // Load user stats
      const userStatsResponse = await adminAPI.users.stats();
      
      // Load job stats
      const jobsResponse = await jobsAPI.list();
      const jobsData = (jobsResponse.data as unknown as PaginatedResponse<ProcessingJob>).results ?? jobsResponse.data as ProcessingJob[];
      const jobs: ProcessingJob[] = Array.isArray(jobsData) ? jobsData : [];
      const completedJobs = jobs.filter((job) => job.status === 'completed').length;
      
      // Load file stats
      const filesResponse = await filesAPI.list();
      const filesData = (filesResponse.data as unknown as PaginatedResponse<FileUpload>).results ?? filesResponse.data as FileUpload[];
      const files: FileUpload[] = Array.isArray(filesData) ? filesData : [];
      
      setStats({
        totalUsers: userStatsResponse.data.total_users,
        activeUsers: userStatsResponse.data.active_users,
        totalJobs: jobs.length,
        completedJobs: completedJobs,
        totalFiles: files.length,
      });
    } catch (error) {
      console.error('Failed to load admin stats:', error);
    } finally {
      setLoading(false);
    }
  };

  // No `color` field any more: these are counts of things that exist, and a
  // green "Total Files" tile claims a verdict it cannot have (ADR-057).
  const statCards = [
    { label: 'Total Users', value: stats.totalUsers, subtext: `${stats.activeUsers} active`, icon: Users },
    { label: 'Total Jobs', value: stats.totalJobs, subtext: `${stats.completedJobs} completed`, icon: Activity },
    { label: 'Total Files', value: stats.totalFiles, subtext: 'Uploaded files', icon: Database },
    { label: 'System Status', value: 'Healthy', subtext: 'All systems operational', icon: Shield },
  ];

  return (
    <div className="max-w-7xl mx-auto">
      {/* The shell header already says "Admin", so this heading is sr-only and
          the role badge stands alone. Both were red — a role you hold is not
          an error, and red here made the whole section read as a warning
          (ADR-057). */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="sr-only">Admin Dashboard</h2>
        <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-bg-subtle px-2.5 py-1 text-sm font-medium text-text-secondary">
          <Shield className="h-4 w-4" aria-hidden="true" />
          Administrator
        </span>
      </div>

      {/* Stats Grid */}
      {loading ? (
        <div className="flex justify-center items-center py-12 mb-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-interactive-primary"></div>
        </div>
      ) : (
        <MetricTileGrid columns={4} className="mb-8">
          {statCards.map((stat) => (
            <MetricTile
              key={stat.label}
              label={stat.label}
              value={stat.value}
              description={stat.subtext}
              IconComponent={stat.icon}
            />
          ))}
        </MetricTileGrid>
      )}

      {/* Admin Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Management */}
        <div className={`${theme.card.background} rounded-lg shadow p-6`}>
          <h2 className={`text-xl font-semibold ${theme.text.primary} mb-4 flex items-center`}>
            <Users className="w-5 h-5 mr-2" />
            User Management
          </h2>
          <p className={`${theme.text.secondary} mb-4`}>
            Manage user accounts, permissions, and access levels.
          </p>
          <Link to="/admin/users" className="block w-full bg-interactive-primary hover:bg-interactive-primary-hover text-text-inverse font-semibold py-2 px-4 rounded-lg transition duration-200 text-center">
            Manage Users
          </Link>
        </div>

        {/* Organization Management */}
        <div className={`${theme.card.background} rounded-lg shadow p-6`}>
          <h2 className={`text-xl font-semibold ${theme.text.primary} mb-4 flex items-center`}>
            <Building2 className="w-5 h-5 mr-2" />
            Organization Management
          </h2>
          <p className={`${theme.text.secondary} mb-4`}>
            Manage organizations and their members.
          </p>
          <Link to="/admin/organizations" className="block w-full bg-interactive-primary hover:bg-interactive-primary-hover text-text-inverse font-semibold py-2 px-4 rounded-lg transition duration-200 text-center">
            Manage Organizations
          </Link>
        </div>

        {/* System Settings */}
        <div className={`${theme.card.background} rounded-lg shadow p-6`}>
          <h2 className={`text-xl font-semibold ${theme.text.primary} mb-4 flex items-center`}>
            <Shield className="w-5 h-5 mr-2" />
            System Settings
          </h2>
          <p className={`${theme.text.secondary} mb-4`}>
            Manage global prompt templates and system configuration.
          </p>
          <Link to="/admin/settings" className="block w-full bg-interactive-primary hover:bg-interactive-primary-hover text-text-inverse font-semibold py-2 px-4 rounded-lg transition duration-200 text-center">
            Configure System
          </Link>
        </div>

        {/* DigiPART Environment */}
        <div className={`${theme.card.background} rounded-lg shadow p-6`}>
          <h2 className={`text-xl font-semibold ${theme.text.primary} mb-4 flex items-center`}>
            <Settings2 className="w-5 h-5 mr-2" />
            DigiPART Environment
          </h2>
          <p className={`${theme.text.secondary} mb-4`}>
            Switch DigiPART between staging, production, or a custom URL — no redeployment needed.
          </p>
          <Link
            to="/admin/system-config"
            className="block w-full bg-interactive-primary hover:bg-interactive-primary-hover text-text-inverse font-semibold py-2 px-4 rounded-lg transition duration-200 text-center"
            data-testid="admin-digipart-config-link"
          >
            Configure Environment
          </Link>
        </div>

        {/* AI Model Configuration */}
        <div className={`${theme.card.background} rounded-lg shadow p-6`}>
          <h2 className={`text-xl font-semibold ${theme.text.primary} mb-4 flex items-center`}>
            <Bot className="w-5 h-5 mr-2" />
            AI Model Configuration
          </h2>
          <p className={`${theme.text.secondary} mb-4`}>
            Configure which AI model each pipeline uses — no redeployment needed.
          </p>
          <Link
            to="/admin/ai-model-config"
            data-testid="nav-ai-model-config"
            className="block w-full bg-interactive-primary hover:bg-interactive-primary-hover text-text-inverse font-semibold py-2 px-4 rounded-lg transition duration-200 text-center"
          >
            Manage AI Models
          </Link>
        </div>

        {/* Activity Logs */}
        <div className={`${theme.card.background} rounded-lg shadow p-6`}>
          <h2 className={`text-xl font-semibold ${theme.text.primary} mb-4 flex items-center`}>
            <Activity className="w-5 h-5 mr-2" />
            Activity Logs
          </h2>
          <p className={`${theme.text.secondary} mb-4`}>
            View all user activities and job history across the system.
          </p>
          <Link to="/admin/activity" className="block w-full bg-qc-success hover:bg-qc-success/90 text-text-inverse font-semibold py-2 px-4 rounded-lg transition duration-200 text-center">
            View Logs
          </Link>
        </div>

        {/* Database Management */}
        <div className={`${theme.card.background} rounded-lg shadow p-6`}>
          <h2 className={`text-xl font-semibold ${theme.text.primary} mb-4 flex items-center`}>
            <Database className="w-5 h-5 mr-2" />
            Database Management
          </h2>
          <p className={`${theme.text.secondary} mb-4`}>
            Manage database backups and maintenance.
          </p>
          <button className="w-full bg-interactive-primary hover:bg-interactive-primary-hover text-text-inverse font-semibold py-2 px-4 rounded-lg transition duration-200">
            Manage Database
          </button>
        </div>
      </div>
    </div>
  );
};

export default Admin;
